import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, BeakerIcon, BoltIcon, ArrowsUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';
import confetti from 'canvas-confetti';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const tourSteps = [
  {
    title: "Welcome to Basket!",
    content: {
      welcome: {
        headline: "Let's first discuss the problem..."
      },
      setup: {
        text: "You've studied. You understood it.",
        subtext: "But as exam day approaches, things start to slip:"
      },
      painPoints: [
        {
          emoji: "😕",
          quote: "I get confused",
          explanation: "everything starts feeling the same"
        },
        {
          emoji: "🔄",
          quote: "I mix things up",
          explanation: "lab values, symptoms, treatments blur together"
        },
        {
          emoji: "😰",
          quote: "I blank out",
          explanation: "even on things I know I studied"
        },
        {
          emoji: "🧹",
          quote: "My brain hits reset",
          explanation: "moving to a new subject wipes out what I just learned"
        }
      ],
      conclusion: "There's too much to review, and nothing sticks when moving to the next subject.",
      insight: "Understanding isn't the issue. Volume is. And the crucial details? They vanish."
    },
    buttonText: "Show Me The Solution",
    type: "problem"
  },
  {
    title: "The Solution",
    content: {
      headline: "The Fix",
      description: "Basket helps you anchor the hardest concepts to distinct memory cues or \"baskets\" — so your brain knows exactly how to recall them under pressure.",
      basketAnatomy: {
        name: "🧺 Memory Trigger or Basket",
        description: "A short yet distinct name that helps you recall everything inside",
        items: [
          {
            id: 1,
            text: "Item #1",
            description: "Your difficult concept of item #1"
          },
          {
            id: 2,
            text: "Item #2",
            description: "Your difficult concept of item #2"
          },
          {
            id: 3,
            text: "Item #3",
            description: "Your difficult concept of item #3"
          }
        ]
      }
    },
    buttonText: "See It In Action",
    type: "solution"
  },
  {
    title: "Here's a Real Example",
    content: {
      headline: "Watch How 3 Items Are Reduced to 1 Memory Trigger",
      description: "Each item reinforces the next. So when you remember one, the rest come with it.",
      example: {
        name: "terminal ileum",
        items: [
          {
            text: "Vitamin B12",
            detail: "absorbed in the terminal ileum",
            connection: "linked to basket name"
          },
          {
            text: "STEMI",
            detail: "Fibrinolysis within 12 hours",
            connection: "12 hrs like 12 in Vitamin B12"
          },
          {
            text: "Marfan syndrome",
            detail: "Upward lens dislocation",
            connection: "upward like ST segment in STEMI"
          }
        ]
      }
    },
    buttonText: "Try It Yourself",
    type: "example"
  },
  {
    title: "🎯 Let's Start With a Real Topic: Trichomoniasis",
    content: {
      concepts: [
        { id: 1, text: "Greenish vaginal discharge", is_highlighted: false },
        { id: 2, text: "Motile protozoa on wet mount", is_highlighted: false },
        { id: 3, text: "Strawberry cervix", is_highlighted: false },
        { id: 4, text: "Treated with metronidazole", is_highlighted: false },
        { id: 5, text: "Sexually transmitted; thus, partner treatment required", is_highlighted: false }
      ],
      tag: "Microbiology"
    },
    type: "topic"
  },
  {
    title: "Turn the Concept Into a Memory Trigger",
    description: "🧠 You're not organizing by logic — you're organizing by memory triggers.",
    prompt: "Pick a short, distinct name that instantly reminds you of the CONCEPT (not the item).",
    inputPlaceholder: "Name your trigger after the concept",
    hint: "Example: \"green\" for greenish discharge, \"10+\" for SBP >10 mmHg, \"Upward\" for upward lens dislocation",
    tooltip: "Pick a short, punchy name that instantly reminds you of the concept. Weird or personal works great.",
    type: "basket-name"
  },
  {
    title: "Wohoo! 🥳 You've Made Your First Memory Trigger!",
    type: "basket-preview"
  },
  {
    title: "Let's Practice Using Your Memory Trigger",
    type: "study-mode"
  },
  {
    title: "See How Your Trigger Can Grow",
    type: "basket-grown",
    description: "We've added a few more items from other subjects that connect to your basket/trigger and item."
  },
  {
    title: "Why This Works",
    type: "works",
    content: {
      explanation: {
        title: "🧠 Why This Works",
        points: [
          {
            emoji: "🔗",
            text: "A <strong>single trigger</strong> helps you remember <em>multiple related items</em> by forming a <strong>connected memory chain</strong>."
          },
          {
            emoji: "✨",
            text: "This <strong>cuts down</strong> the number of <em>scattered facts</em> you need to memorize."
          },
          {
            emoji: "💪",
            text: "It's built around the <strong>concepts you struggle with</strong>, <em>not just what you already know</em>"
          }
        ]
      }
    }
  },
  {
    title: "🥳 Tutorial Completed!",
    type: "finish"
  }
];

const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-1 {
    animation: fadeIn 0.3s ease-out forwards;
    animation-delay: 0.1s;
  }

  .animate-fade-in-2 {
    animation: fadeIn 0.5s ease-out forwards;
    animation-delay: 0.6s;
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;

const OnboardingTour = ({ isOpen, onClose, userId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [basketName, setBasketName] = useState('');
  const [debouncedBasketName, setDebouncedBasketName] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedPath, setSelectedPath] = useState(null);
  const [concepts, setConcepts] = useState([
    { id: 1, text: "Greenish vaginal discharge", is_highlighted: false },
    { id: 2, text: "Motile protozoa on wet mount", is_highlighted: false },
    { id: 3, text: "Strawberry cervix", is_highlighted: false },
    { id: 4, text: "Treated with metronidazole", is_highlighted: false },
    { id: 5, text: "Sexually transmitted; thus, partner treatment required", is_highlighted: false }
  ]);
  const [isRevealed, setIsRevealed] = useState(false);
  const [revealedItems, setRevealedItems] = useState([false, false, false]);
  const [rating, setRating] = useState(0);
  const [isAddingToWorkspace, setIsAddingToWorkspace] = useState(false);
  const [basketAddedToWorkspace, setBasketAddedToWorkspace] = useState(false);

  // New state variables for tour tracking
  const [tourId, setTourId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);

  const navigate = useNavigate();

  // Initialize tour
  useEffect(() => {
    const initializeTour = async () => {
      if (!userId || !isOpen) return;

      try {
        // Create new tour
        const { data: newTour, error: createError } = await supabase
          .from('onboarding_tours')
          .insert([{
            user_id: userId,
            started_at: new Date().toISOString(),
            last_step_completed: 0
          }])
          .select()
          .single();

        if (createError) throw createError;
        setTourId(newTour.id);
      } catch (error) {
        console.error('Error initializing tour:', error);
        toast.error('Failed to start tutorial. Please try again.');
      }
    };

    initializeTour();
  }, [userId, isOpen]);

  // Add debounce effect for basket name
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBasketName(basketName);
    }, 500); // Wait for 500ms of no typing

    return () => clearTimeout(timer);
  }, [basketName]);

  const handleNext = async () => {
    if (currentStep < tourSteps.length - 1) {
      // If we're on the works step and moving to finish
      if (step.type === 'works' && !isAddingToWorkspace) {
        try {
          // Mark tour as completed when basket is added
          const { error: tourError } = await supabase
            .from('onboarding_tours')
            .update({ 
              completed_at: new Date().toISOString(),
              added_to_basket: true,
              basket_name: basketName.trim()
            })
            .eq('id', tourId);

          if (tourError) throw tourError;

          // Update user_profiles to mark onboarding as completed
          const { error: profileError } = await supabase
            .from('user_profiles')
            .update({
              onboarding_tour_completed: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);

          if (profileError) throw profileError;

          setBasketAddedToWorkspace(true);
        } catch (error) {
          console.error('Error updating completion status:', error);
        }
      }
      
      setCurrentStep(currentStep + 1);
      setCurrentSlide(0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      // Reset revealed states when navigating
      setIsRevealed(false);
      if (tourSteps[currentStep - 1].type === 'works') {
        setRevealedItems([false, false, false]);
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    if (isSubmittingFeedback) return;
    setIsSubmittingFeedback(true);

    try {
      // Only update rating and feedback if provided
      const updateData = {};

      if (rating >= 1 && rating <= 5) {
        updateData.rating = rating;
      }
      if (feedback.trim()) {
        updateData.feedback = feedback.trim();
      }

      // Only update if we have rating or feedback
      if (Object.keys(updateData).length > 0) {
        const { error: tourError } = await supabase
          .from('onboarding_tours')
          .update(updateData)
          .eq('id', tourId);

        if (tourError) throw tourError;
      }

      // Get workspace and navigate
      const { data: workspaces, error: workspaceError } = await supabase
        .from('workspaces')
        .select('id, name')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (workspaceError) throw workspaceError;
      if (!workspaces || workspaces.length === 0) {
        throw new Error('No workspace found');
      }

      const workspace = workspaces[0];
      onClose();
      navigate(`/workspace/${workspace.id}`, { 
        state: { workspaceName: workspace.name }
      });
    } catch (error) {
      console.error('Error completing tour:', error);
      toast.error('Failed to complete tour. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleToggleImportance = (conceptId) => {
    setConcepts(prevConcepts => prevConcepts.map(concept => ({
      ...concept,
      is_highlighted: concept.id === conceptId ? !concept.is_highlighted : false
    })));
    
    // Update selectedConcept with the full concept object
    const concept = concepts.find(c => c.id === conceptId);
    setSelectedConcept(concept);
  };

  const handleItemClick = (index) => {
    setRevealedItems(prev => {
      const newState = [...prev];
      newState[index] = true;

      // Check if this click revealed all items
      if (newState.every(item => item)) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#f43f5e', '#fecdd3', '#fff1f2'],
        });
      }

      return newState;
    });
  };

  const getItemContent = (index) => {
    if (selectedConcept?.text.includes("Sexually transmitted;")) {
      switch(index) {
        case 0:
          return {
            topic: "Trichomoniasis",
            relation_description: "linked to basket name",
            tag: "Microbiology",
            concept: selectedConcept.text,
            color: "emerald"
          };
        case 1:
          return {
            topic: "Molluscum contagiosum",
            relation_description: "linked to trichomoniasis",
            tag: "Microbiology",
            concept: "can be sexually transmitted",
            color: "violet"
          };
        case 2:
          return {
            topic: "Partner Notification Program",
            relation_description: "done for STIs like trichomoniasis",
            tag: "Ethics",
            concept: "notified if infected partner refuses/fails to inform susceptible partner within a specific time frame",
            color: "lime"
          };
        default:
          return null;
      }
    } else if (selectedConcept?.text.includes("protozoa")) {
      switch(index) {
        case 0:
          return {
            topic: "Trichomoniasis",
            relation_description: "linked to basket name",
            tag: "Microbiology",
            concept: selectedConcept.text,
            color: "emerald"
          };
        case 1:
          return {
            topic: "Metronidazole",
            relation_description: "treatment for trichomoniasis",
            tag: "Pharmacology",
            concept: "no alcohol 48 hours after due to disulfiram-like reaction",
            color: "yellow"
          };
        case 2:
          return {
            topic: "Disulfiram",
            relation_description: "linked to metronidazole",
            tag: "Pharmacology",
            concept: "contraindicated in ischemic heart disease and psychosis",
            color: "purple"
          };
        default:
          return null;
      }
    } else if (selectedConcept?.text.includes("Strawberry")) {
      switch(index) {
        case 0:
          return {
            topic: "Trichomoniasis",
            relation_description: "linked to basket name",
            tag: "Microbiology",
            concept: selectedConcept.text,
            color: "emerald"
          };
        case 1:
          return {
            topic: "Kawasaki Disease",
            relation_description: "like trichomoniasis has 'strawberry' characteristic",
            tag: "Paediatrics",
            concept: "Coronary aneurysm prevention: high-dose aspirin, IVIG",
            color: "rose"
          };
        case 2:
          return {
            topic: "Methotrexate",
            relation_description: "linked to Kawasaki through high-dose aspirin",
            tag: "Pharmacology",
            concept: "Caution: High-dose ASA (↓MTX excretion → toxicity)",
            color: "sky"
          };
        default:
          return null;
    }
    } else if (selectedConcept?.text.includes("discharge")) {
      switch(index) {
        case 0:
          return {
            topic: "Trichomoniasis",
            relation_description: "linked to basket name",
            tag: "Microbiology",
            concept: selectedConcept.text,
            color: "emerald"
          };
        case 1:
          return {
            topic: "Pseudomonas",
            relation_description: "like trichomoniasis has greenish coloration",
            tag: "Microbiology",
            concept: "produces Pyocyanin which gives its blue-green pigment",
            color: "orange"
          };
        case 2:
          return {
            topic: "Ceftazidime",
            relation_description: "treatment for pseudomonas",
            tag: "Pharmacology",
            concept: "a 3rd generation Cephalosporin used in Pseudomonas infections",
            color: "red"
          };
        default:
          return null;
      }
    } else if (selectedConcept?.text.includes("metronidazole")) {
      switch(index) {
        case 0:
          return {
            topic: "Trichomoniasis",
            relation_description: "linked to basket name",
            tag: "Microbiology",
            concept: selectedConcept.text,
            color: "emerald"
          };
        case 1:
          return {
            topic: "CYP450 Inhibitors",
            relation_description: "linked to trichomoniasis through its metronidazole tx",
            tag: "Pharmacology",
            concept: "- SICKFACES.COM; metronidazole",
            color: "indigo"
          };
        case 2:
          return {
            topic: "Halitosis",
            relation_description: "linked to trichomoniasis through its metronidazole tx",
            tag: "Palliative Care",
            concept: "palliative management: metronidazole, antiseptic mouthwash",
            color: "fuchsia"
          };
        default:
          return null;
      }
    }
    
    // Default fallback for any unhandled cases
    return {
      topic: "Trichomoniasis",
      tag: "Microbiology",
      concept: selectedConcept?.text || "Loading...",
      color: "emerald"
    };
  };

  const renderProblem = () => {
    const { content } = tourSteps[0];
    
    return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Welcome & Setup */}
        <div className="text-center space-y-3">
          <div>
            <h2 className="text-lg font-bold text-neutral-800 mb-1">
              {content.welcome.headline}
            </h2>
          </div>
          <div className="bg-gradient-to-r from-neutral-50 to-butter-50 rounded-lg p-3 border border-neutral-200 inline-block">
            <p className="text-base font-semibold text-neutral-900">
              {content.setup.text}
            </p>
            <p className="text-sm text-neutral-600">
              {content.setup.subtext}
            </p>
          </div>
        </div>

        {/* Pain Points */}
        <div className="space-y-2">
          {content.painPoints.map((point, index) => (
            <div 
              key={index}
              className="bg-gradient-to-r from-white to-neutral-50 rounded-lg p-2 border-l-4 border-neutral-300 shadow-sm hover:shadow-md transition-all duration-200 hover:border-l-cranberry-500"
            >
              <div className="flex items-start">
                <span className="text-lg mr-2" role="img" aria-label="emotion">
                  {point.emoji}
                </span>
                <span className="text-sm font-medium text-neutral-800 mr-2">
                  {point.quote}
                </span>
                <span className="text-xs text-neutral-600">
                  — {point.explanation}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Conclusion & Insight */}
        <div className="bg-gradient-to-br from-neutral-50 via-butter-50 to-neutral-50 rounded-lg p-3">
          <p className="text-sm text-neutral-700">
            {content.conclusion}
          </p>
          <p className="text-base font-semibold text-neutral-900 border-t border-neutral-200/50 pt-2 mt-2">
            {content.insight}
          </p>
        </div>
      </div>
    );
  };

  const renderSolution = () => {
    const step = tourSteps[currentStep];
    
    return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Solution Header */}
        <div className="bg-gradient-to-br from-neutral-50 to-butter-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span role="img" aria-label="light bulb" className="text-lg">💡</span>
            <h2 className="text-lg font-bold text-neutral-900">
              The Solution
            </h2>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-neutral-800">
              Anchor hard-to-remember concepts to <strong>distinct memory triggers</strong> — or <em>"baskets."</em>
            </p>
            <p className="text-base text-neutral-800">
              These baskets organize items based on <strong>memory triggers</strong> and <strong>logic</strong>, forming a memory chain that is easier to recall <em>despite the high volume of information</em>.
            </p>
          </div>
        </div>

        {/* Basket Anatomy */}
        <div className="bg-white rounded-lg p-2 pt-2 w-full sm:w-[360px] border-2 border-neutral-900 shadow-sm h-[240px] flex flex-col mx-auto">
          {/* Basket Header */}
          <div className="flex justify-between items-start relative mb-0">
            <div className="flex items-start space-x-1.5">
              <span className="text-base">🧺</span>
        <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  Memory Trigger or "Basket"
          </h3>
                <p className="text-xs text-neutral-600">
                  A short, distinct name that helps you recall everything inside
                </p>
              </div>
            </div>
        </div>

          {/* Topics Grid */}
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            {step.content.basketAnatomy.items.map((item) => (
              <div key={item.id} className="bg-neutral-50 rounded-lg p-1.5">
                <div className="font-medium text-neutral-900 mb-0.5 text-xs">
                  {item.text}
                </div>
                <div className="text-[10px] text-blue-600">
                  {item.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Memory Chain Visualization */}
        <div className="bg-neutral-50 rounded-lg p-2 border border-neutral-200">
          <h4 className="text-xs font-medium text-neutral-900 mb-1">Created Memory Chain</h4>
          <div className="flex items-center space-x-1 pt-2">
            {/* Trigger */}
            <div className="px-1.5 py-0.5 bg-neutral-100 border border-neutral-900 rounded text-[10px] font-semibold text-neutral-900 relative">
              Basket Name
              <div className="absolute -top-2.5 -right-1">
                <div className="bg-cranberry-600 text-white px-1 py-0.5 rounded text-[8px] font-medium shadow-sm">
                  Trigger
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center">
              <ArrowsUpDownIcon className="h-2.5 w-2.5 text-neutral-400 rotate-90" />
            </div>

            {/* Item 3 */}
            <div className="px-1.5 py-0.5 bg-blue-50 border border-blue-200 rounded text-[10px] font-medium text-neutral-900">
              Item #3
            </div>

            {/* Arrow */}
            <div className="flex items-center">
              <ArrowsUpDownIcon className="h-2.5 w-2.5 text-neutral-400 rotate-90" />
            </div>

            {/* Item 1 */}
            <div className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] font-medium text-neutral-900">
              Item #1
            </div>

            {/* Arrow */}
            <div className="flex items-center">
              <ArrowsUpDownIcon className="h-2.5 w-2.5 text-neutral-400 rotate-90" />
            </div>

            {/* Item 2 */}
            <div className="px-1.5 py-0.5 bg-purple-50 border border-purple-200 rounded text-[10px] font-medium text-neutral-900">
              Item #2
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderExample = () => {
    const step = tourSteps[currentStep];
    const example = step.content.example;
    
    return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Example Header */}
        <div className="bg-gradient-to-br from-neutral-50 to-butter-50 rounded-lg p-2">
          <div className="flex items-center space-x-1.5">
            <span role="img" aria-label="example" className="text-base">🔍</span>
            <h2 className="text-sm font-bold text-neutral-900">
              {step.content.headline}
            </h2>
          </div>
          <p className="text-xs text-neutral-700">
            {step.content.description}
          </p>
        </div>

        {/* Terminal Ileum Basket */}
        <div className="bg-white rounded-lg p-2 w-full sm:w-[360px] border border-neutral-900 shadow-sm h-[240px] flex flex-col mx-auto">
          {/* Basket Header */}
          <div className="flex justify-between items-start relative">
            <div className="flex items-start space-x-1.5">
              <span className="text-base">🧺</span>
              <h3 className="text-sm font-semibold text-neutral-900">
                {example.name}
                </h3>
            </div>
          </div>

          {/* Topics Grid */}
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            {example.items.map((item, index) => (
              <div key={index} className="bg-neutral-50 rounded-lg p-1.5">
                <div className="font-medium text-neutral-900 mb-0.5 text-xs">
                  {item.text}
              </div>
                <div className="text-[10px] text-blue-600">
                  {item.detail}
              </div>
                <div className="mt-0.5 text-[10px] bg-yellow-100 text-yellow-800 p-1 rounded">
                  {item.connection}
                </div>
              </div>
            ))}
              </div>
            </div>

        {/* Connection Explanation */}
        <div className="bg-butter-50 rounded-lg p-1.5 text-neutral-800">
          {/* Memory Chain Visualization */}
          <div className="mb-1">
            <h4 className="text-xs font-medium text-neutral-900 mb-1">Memory Chain</h4>
            <div className="flex items-center space-x-1">
              {/* Terminal Ileum */}
              <div className="px-1.5 py-0.5 bg-neutral-100 border border-neutral-900 rounded relative">
                <span className="text-[10px] font-semibold text-neutral-900">Terminal Ileum</span>
                <div className="absolute -top-1 -right-1">
                  <div className="bg-cranberry-600 text-white px-1 py-0.5 rounded text-[8px] font-medium shadow-sm">
                    Trigger
              </div>
              </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowsUpDownIcon className="h-2.5 w-2.5 text-neutral-400 rotate-90" />
            </div>

              {/* Vitamin B12 */}
              <div className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded">
                <span className="text-[10px] font-medium text-neutral-900">Vitamin B12</span>
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowsUpDownIcon className="h-2.5 w-2.5 text-neutral-400 rotate-90" />
              </div>

              {/* STEMI */}
              <div className="px-1.5 py-0.5 bg-blue-50 border border-blue-200 rounded">
                <span className="text-[10px] font-medium text-neutral-900">STEMI</span>
            </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowsUpDownIcon className="h-2.5 w-2.5 text-neutral-400 rotate-90" />
              </div>

              {/* Marfan */}
              <div className="px-1.5 py-0.5 bg-purple-50 border border-purple-200 rounded">
                <span className="text-[10px] font-medium text-neutral-900">Marfan Syndrome</span>
              </div>
          </div>
        </div>

          <ul className="space-y-0.5 text-[10px] pl-2">
            <li><strong>Terminal ileum</strong> acts as the memory trigger for <strong>Vitamin B12</strong> — helping you recall where it's <em>absorbed</em>.</li>
            <li><strong>STEMI</strong> gets linked in through the <strong>"12"</strong> in B12, <em>reminding you</em> of its <strong>12-hour window</strong> for fibrinolysis.</li>
            <li><strong>Marfan syndrome</strong> joins the chain by associating its <strong>upward</strong> lens dislocation with <strong>ST elevation</strong> in STEMI.</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderTopic = () => {
    const step = tourSteps[currentStep];
    const selectedConcept = concepts.find(c => c.is_highlighted);
    
    return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Question and Instructions */}
        <div className="bg-butter-50 rounded-lg border border-neutral-900 p-2">
          <h3 className="text-base font-bold text-neutral-900 mb-1">
            🔍 Which Detail Trips You Up?
          </h3>
          <p className="text-xs text-neutral-700">
            Click the ⭐ next to any concept you find hard to remember.
          </p>
          <p className="text-xs text-neutral-700">
            You'll turn this concept into a memory trigger later.
          </p>
        </div>

        {/* Topic Display */}
        <div className="bg-white rounded-lg border-2 border-neutral-900 p-2">
          <div className="mb-2">
            <h2 className="text-base font-bold text-neutral-900">
              Trichomoniasis
            </h2>
            <span className="inline-block px-2 py-0.5 text-xs rounded-full mt-1 border border-neutral-900 bg-green-100 text-green-800">
              {step.content.tag}
            </span>
          </div>

          <ul className="space-y-1">
            {concepts.map((concept) => (
              <li
                key={concept.id}
                className={`flex items-start justify-between p-1.5 rounded-md transition-colors duration-200 
                  ${concept.is_highlighted ? 'bg-yellow-50 border border-yellow-200' : ''}
                  hover:bg-blue-50 relative pl-6`}
              >
                <span className="absolute left-2 top-[0.4rem] text-gray-600 dark:text-gray-400 text-base leading-none">
                  •
                </span>
                <div className={`text-neutral-900 flex-grow text-xs ${concept.is_highlighted ? 'font-medium' : ''}`}>
                  {concept.text}
                  </div>
                <div className="flex items-start space-x-1 ml-1">
                  <button
                    onClick={() => handleToggleImportance(concept.id)}
                    className="p-0.5 rounded-full hover:bg-yellow-100 transition-colors"
                    title={concept.is_highlighted ? "Remove importance" : "Mark as most important"}
                  >
                    <svg
                      className={`w-[14px] h-[14px] transition-colors ${
                        concept.is_highlighted
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-400 hover:text-yellow-400'
                      }`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Note Section - Only shown when a concept is selected */}
        {selectedConcept && (
          <div className="px-1 text-xs opacity-0 animate-fade-in">
            <div className="space-y-1">
              <div className="flex items-start">
                <span className="font-medium text-neutral-900 w-24">Item:</span>
                <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Trichomoniasis</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium text-neutral-900 w-24">Difficult concept:</span>
                <span className="text-blue-600">{selectedConcept.text}</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium text-neutral-900 w-24">Memory Trigger/Basket:</span>
                <span className="text-neutral-600 italic">(You'll create this in the next step)</span>
              </div>
            </div>
            <div className="mt-2 flex items-start gap-1.5 bg-butter-50 rounded-lg p-2">
              <span role="img" aria-label="tip" className="text-base mt-0.5">💡</span>
              <p className="text-neutral-700">
                Tip: If you have multiple difficult concepts, just start with one. You can always come back and revise your memory triggers later.
          </p>
        </div>
          </div>
        )}
      </div>
    );
  };

  const renderStepContent = () => {
    const step = tourSteps[currentStep];

    switch (step.type) {
      case 'problem':
        return renderProblem();
      case 'solution':
        return renderSolution();
      case 'example':
        return renderExample();
      case 'topic':
        return renderTopic();
      case 'basket-name':
        if (!selectedConcept) {
          setCurrentStep(tourSteps.findIndex(s => s.type === 'topic'));
          return null;
        }
        return renderBasketName();
      case 'basket-preview':
        return renderBasketPreview();
      case 'study-mode':
        const triggerConfetti = () => {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: ['#f43f5e', '#fecdd3', '#fff1f2'],
          });
        };

        return (
          <div className="space-y-6 flex flex-col items-center">
            {/* Study Mode Header */}
            <div className="bg-butter-50 rounded-xl p-4 border border-butter-200 w-full sm:w-[480px]">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🧠</div>
            <div className="space-y-2">
                  <h3 className="font-semibold text-neutral-900">Quick Quiz:</h3>
                  <p className="text-neutral-700">Try to recall what's inside the {basketName} basket.</p>
                  <div>
                    <p className="text-neutral-700 text-sm mb-1.5">Before you reveal the answer, ask yourself:</p>
                    <ol className="space-y-1 text-neutral-700 list-decimal list-inside pl-1">
                      <li><span className="font-semibold">WHAT</span> topic did I place in this basket?</li>
                      <li><span className="font-semibold">WHY</span> did I place it in this basket?</li>
                    </ol>
                  </div>
            </div>
          </div>
            </div>

            {/* Basket Card with Hidden/Revealed Content */}
            <div className="bg-white rounded-xl p-4 pt-2 border-2 border-neutral-900 shadow-sm h-[240px] w-full sm:w-[360px] flex flex-col">
              <div className="flex justify-between items-start relative mb-0">
                <div className="flex items-start space-x-2">
                  <span className="text-2xl">🧺</span>
                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900">
                      {basketName}
                    </h3>
            </div>
            </div>
                <div className="ml-auto">
                  <div className="bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full text-xs">
                    1 topic
            </div>
          </div>
              </div>

              {/* Hidden/Revealed Topic */}
              <div className="mt-4 flex-grow flex items-center justify-center">
                {!isRevealed ? (
                  <div 
                    onClick={() => {
                      setIsRevealed(true);
                      triggerConfetti();
                    }}
                    className="cursor-pointer transition-colors duration-200 
                              bg-neutral-100 hover:bg-neutral-200
                              rounded-lg border-2 border-dashed border-neutral-300
                              flex items-center justify-center
                              w-[280px] h-[100px]"
                  >
                    <div className="text-center p-4">
                      <div className="text-neutral-600 font-medium text-lg mb-2">Topic Hidden</div>
                      <div className="text-neutral-400 text-base">Think first, then tap to reveal</div>
                  </div>
              </div>
                ) : (
                  <div className="bg-neutral-50 rounded-lg p-4 w-[280px] h-[100px] flex flex-col justify-center">
            <div className="text-center">
                      <div className="inline-block">
                        <div className="font-medium text-neutral-900 text-sm bg-emerald-50 border-2 border-emerald-200 rounded-lg px-3 py-1.5 shadow-sm">
                          Trichomoniasis
            </div>
            </div>
                      <div className="text-blue-600 text-base mt-3">
                        {selectedConcept?.text}
          </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Helper Text */}
            <div className="mt-3">
    
                <p className="text-sm text-neutral-600 text-center">
                  In study mode, Basket hides your concepts so you can quiz yourself. When you revisit this basket, you'll recall not just the item in it but why it was hard for you.
              </p>

            </div>
          </div>
        );
      case 'basket-grown':
        return renderBasketGrown();
      case 'works':
        return renderWorks();
      case 'summary':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              {step.achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 text-sm"
                >
                  <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                  <span>{achievement}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-sm font-medium text-cranberry-600">
              {step.footer}
            </p>
          </div>
        );
      case 'finish':
        return renderFinish();
      default:
        return (
          <div className="text-center text-neutral-600">
            <p className="mb-4">{step.description}</p>
          </div>
        );
    }
  };

  const renderWorks = () => {
    const { explanation } = tourSteps[currentStep].content;
    
    return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Explanation Section */}
        <div className="opacity-0 animate-fade-in">
          <div className="space-y-2 bg-neutral-50 rounded-lg p-2 border border-neutral-200">
            <div className="flex items-start gap-1.5">
              <span role="img" aria-label="brain" className="text-base">🧠</span>
              <h3 className="font-semibold text-neutral-900 text-sm">Why This Works</h3>
                </div>
            <div className="space-y-1.5 pl-6">
              {explanation.points.map((point, index) => (
                <div key={index} className="flex items-start gap-1">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-4 h-4 flex items-center justify-center text-base">
                      {point.emoji}
                    </div>
                  </div>
                  <p className="text-[10px] text-neutral-700" dangerouslySetInnerHTML={{ __html: point.text }} />
                </div>
              ))}
            </div>
          </div>
              </div>

        {/* Study Mode Basket */}
        <div className="mt-2">
          {/* Header */}
          <div className="text-center mb-1">
            <h3 className="text-sm font-semibold text-neutral-900">What are the 3 items again?</h3>
          </div>

          <div className="bg-white rounded-lg p-2 pt-2 w-full sm:w-[420px] h-[280px] border-2 border-neutral-900 shadow-sm flex flex-col mx-auto">
            <div className="flex justify-between items-start relative mb-0">
              <div className="flex items-start space-x-1.5">
                <span className="text-base">🧺</span>
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900">
                    {basketName}
                </h3>
                </div>
              </div>
              <div className="ml-auto">
                <div className="bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full text-[8px]">
                  3 items
                </div>
              </div>
              </div>

            {/* Topics Grid */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {revealedItems.map((isRevealed, index) => {
                const itemContent = getItemContent(index);
                return (
                  <div
                    key={index}
                    onClick={() => handleItemClick(index)}
                    className={`
                      cursor-pointer transition-all duration-200 rounded-lg
                      ${isRevealed ? 'bg-neutral-50' : 'bg-neutral-100 hover:bg-neutral-200'}
                      ${isRevealed ? '' : 'border border-dashed border-neutral-300'}
                      flex items-center justify-center h-[100px]
                    `}
                  >
                    {isRevealed ? (
                      <div className="p-2 w-full">
                        <div className="inline-block">
                          <div className={`font-medium text-neutral-900 text-xs bg-${itemContent.color}-50 border border-${itemContent.color}-200 rounded-lg px-2 py-1 shadow-sm`}>
                            {itemContent.topic}
                    </div>
                  </div>
                        <div className="text-[10px] text-blue-600 mt-1 leading-tight">
                          {itemContent.concept}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center p-1.5">
                        <div className="text-neutral-600 font-medium text-xs mb-0.5">Topic Hidden</div>
                        <div className="text-neutral-400 text-[10px]">Think first, then tap to reveal</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
              </div>

          {/* Helper Text */}
          <div className="mt-1">
            {!revealedItems.every(item => item) ? (
              <p className="text-[10px] text-neutral-600 text-center">
                In study mode, Basket hides your concepts so you can quiz yourself. When you revisit this basket, you'll recall not just the item in it — but why it was hard for you.
              </p>
            ) : (
              <div className="opacity-0 animate-fade-in">
                <div className="bg-butter-50 rounded-lg p-2 border border-butter-200">
                  <div className="flex items-start gap-1.5">
                    <span role="img" aria-label="tip" className="text-base">🤓</span>
                    <div className="space-y-0.5">
                      <p className="font-medium text-neutral-900 text-xs">Pro-tip: Use it in your practice tests, readings, and exams</p>
                      <div className="space-y-0.5 text-[10px] text-neutral-700">
                        <p>When you encounter a topic, ask yourself:</p>                   
                        <p><span className="text-base mr-1">🧺</span> What's the trigger/basket for this?</p>
                        <p><span className="text-base mr-1">🍎</span> What other items are connected in the basket?</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
        );
  };

  const renderFinish = () => {
        return (
      <div className="space-y-3 w-full sm:w-[480px] mx-auto">
        {/* Merged Completion Header & Achievements */}
        <div className="bg-gradient-to-br from-butter-50 to-neutral-50 rounded-lg p-3 border border-butter-200">
          <div className="space-y-3">
            {/* Header */}
            <div className="flex items-start gap-2">
              <span role="img" aria-label="strong" className="text-lg">👏</span>
              <div>
                <h3 className="font-semibold text-neutral-900 text-base">Good job!You've learned how to apply memory triggers!</h3>
              </div>
            </div>

            {/* Achievements */}
            <div className="pl-7">
            <div className="space-y-2">
                {/* Step 1 */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-neutral-900 text-xs">
                    <span className="text-sm">✅</span>
                    <span className="font-medium">Step 1</span>
                  </div>
                  <div className="pl-6">
                    <div className="text-neutral-900 text-xs">Targeted your hard concept</div>
                    <div className="text-blue-600 text-xs mt-0.5 pl-2">"{selectedConcept?.text}"</div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-neutral-900 text-xs">
                    <span className="text-sm">✅</span>
                    <span className="font-medium">Step 2</span>
                  </div>
                  <div className="pl-6">
                    <div className="text-neutral-900 text-xs">Created your memory trigger ("basket") based on hard concept</div>
                    <div className="text-blue-600 text-xs mt-0.5 pl-2">"{basketName}"</div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-neutral-900 text-xs">
                    <span className="text-sm">✅</span>
                    <span className="font-medium">Step 3</span>
                  </div>
                  <div className="pl-6">
                    <div className="text-neutral-900 text-xs">Linked related topics to your trigger</div>
                    <div className="text-blue-600 text-xs mt-0.5 pl-2">
                      {selectedConcept?.text.includes("protozoa") ? (
                        '"Trichomoniasis, Metronidazole, Disulfiram"'
                      ) : selectedConcept?.text.includes("Strawberry") ? (
                        '"Trichomoniasis, Kawasaki Disease, Methotrexate"'
                      ) : selectedConcept?.text.includes("discharge") ? (
                        '"Trichomoniasis, Pseudomonas, Ceftazidime"'
                      ) : selectedConcept?.text.includes("metronidazole") ? (
                        '"Trichomoniasis, CYP450 Inhibitors, Halitosis"'
                      ) : selectedConcept?.text.includes("Sexually transmitted") ? (
                        '"Trichomoniasis, Molluscum contagiosum, Partner Notification Program"'
                      ) : ''}
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-neutral-900 text-xs">
                    <span className="text-sm">✅</span>
                    <span className="font-medium">Step 4</span>
                  </div>
                  <div className="pl-6">
                    <div className="text-neutral-900 text-xs">Practiced recalling items using your trigger</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Workspace Success Message */}
        {basketAddedToWorkspace && (
          <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-200">
            <p className="text-emerald-700 text-xs flex items-center gap-1.5">
              <span role="img" aria-label="gift" className="text-base">🎁</span>
              As reward for completing the tutorial, we've added the "terminal ileum" and "{basketName}" baskets to your workspace. That's 6 topics reduced to 2!
            </p>
          </div>
        )}

        {/* Rating & Feedback */}
        <div className="p-2">
          <div className="space-y-3">
            <div className="text-center">
              <p className="font-medium text-neutral-900 text-sm mb-1">How was the tutorial? 🤔</p>
              <div className="flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                    disabled={isSubmittingFeedback}
                  >
                    <span 
                      className={`text-xl ${rating >= star ? 'text-yellow-400' : 'text-white'}`}
                      style={{ 
                        textShadow: rating >= star 
                          ? '0 0 2px #000, 0 0 2px #000, 0 0 2px #000, 0 0 2px #000' 
                          : '0 0 2px #666, 0 0 2px #666, 0 0 2px #666, 0 0 2px #666'
                      }}
                    >
                      ★
                    </span>
                </button>
              ))}
              </div>
            </div>
            {rating > 0 && (
              <div className="space-y-1.5 animate-fade-in">
                <p className="text-center font-medium text-neutral-900 text-xs">Thanks! We'd love to hear your thoughts:</p>
                <div className="relative flex items-center gap-1.5">
                  <div className="relative flex-grow">
                    <input
                      type="text"
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Share your feedback, suggestions, or ideas...(optional)"
                      className="w-full px-3 py-2 text-xs bg-white/80 border-2 border-neutral-200 rounded-lg 
                              focus:border-neutral-300 focus:ring-1 focus:ring-neutral-300 
                              text-neutral-900 placeholder-neutral-500"
                      disabled={isSubmittingFeedback}
                      autoFocus
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        );
  };

  const renderBasketName = () => {
    if (!selectedConcept) {
      setCurrentStep(tourSteps.findIndex(s => s.type === 'topic'));
      return null;
    }
        return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Selected Item Info */}
        <div className="bg-white rounded-lg border-2 border-neutral-200 p-2">
          <h3 className="font-medium text-neutral-900 mb-2 text-sm">You're studying:</h3>
          <div className="space-y-1">
            <div className="flex items-start">
              <span className="font-medium text-neutral-900 w-24 text-xs">Item:</span>
              <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs">Trichomoniasis</span>
            </div>
            <div className="flex items-start">
              <span className="font-medium text-neutral-900 w-24 text-xs">Difficult concept:</span>
              <span className="text-blue-600 text-xs">{selectedConcept?.text || "No concept selected"}</span>
            </div>
          </div>
        </div>

        {/* Memory Trigger Input Section */}
        <div className="bg-butter-50 rounded-lg border-2 border-neutral-900 p-3">
            <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span role="img" aria-label="brain" className="text-lg">🧠</span>
              <label htmlFor="basketName" className="text-base font-semibold text-neutral-900">
                {step.prompt}
              </label>
            </div>
              <input
              id="basketName"
                type="text"
                value={basketName}
                onChange={(e) => setBasketName(e.target.value)}
              placeholder="Name your memory trigger after the concept..."
              autoFocus
              className="w-full px-3 py-2 rounded-lg border-2 border-neutral-900 text-neutral-900 placeholder-neutral-400 text-sm shadow-sm"
              />
            <div className="flex items-start gap-1.5 mt-1">
              <span role="img" aria-label="tip" className="text-base">💡</span>
              <div className="text-xs text-neutral-700 space-y-1">
                <p>{step.hint}</p>
                <div className="space-y-0.5">
                  <p className="flex items-center gap-1">
                    <span className="text-red-500">❌</span> subject names (e.g. surgery, derma, ortho, etc)
                  </p>
                  <p className="flex items-center gap-1">
                    <span className="text-red-500">❌</span> topic names (e.g. trichomoniasis)
                  </p>
            </div>
            </div>
          </div>
          </div>
        </div>

        {/* Tooltip - Trigger Locked Message */}
        {debouncedBasketName.trim() && (
          <div className="bg-neutral-50 rounded-lg p-2 border border-neutral-200 opacity-0 animate-fade-in">
              <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <span role="img" aria-label="link" className="text-base">⚡️</span>
                <h3 className="font-semibold text-neutral-900 text-sm">Trigger Locked In!</h3>
                  </div>
              <div className="space-y-2 pl-6">
                <p className="text-xs text-neutral-700">
                  You chose <span className="font-medium">"{debouncedBasketName}"</span> as your memory trigger for <span className="text-green-700">Trichomoniasis</span>. This will be your basket name. Note: <strong>memory trigger = basket name</strong>.
                </p>
                <p className="text-xs text-neutral-600">
                  With repetition, your brain will connect <span className="text-green-700">Trichomoniasis</span> to <span className="font-medium">"{debouncedBasketName}"</span> to help you recall your difficult concept <span className="text-blue-600">{selectedConcept?.text}</span>.
                </p>
              </div>
            </div>
            </div>
        )}
          </div>
        );
  };

  const renderBasketPreview = () => {
        return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Description */}
        <div className="bg-butter-50 rounded-lg p-2 border border-butter-200">
          <p className="text-xs text-neutral-800">
            Here's how your basket will appear. Simply search for the basket name, its items or concept when you want to recall the trigger and its items.
          </p>
                  </div>

        {/* Container with black border */}
        <div className="border border-neutral-900 rounded-lg p-2 overflow-hidden">
          {/* Search Section - No animation */}
          <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 mb-2">
            <div className="flex-grow relative">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search baskets, topics, or concepts..."
                  className="w-full px-3 py-1.5 pr-8 text-xs rounded-lg border-2 border-neutral-200 focus:border-cranberry-600 focus:ring-1 focus:ring-cranberry-600 text-neutral-900 placeholder-neutral-400"
                  disabled
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <svg className="h-4 w-4 text-neutral-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                    <path d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" />
                  </svg>
              </div>
            </div>
            </div>
                <button
              disabled
              className="h-8 px-3 bg-cranberry-700/50 text-white text-xs rounded-lg border-2 border-neutral-400 font-medium cursor-not-allowed"
            >
              + New Basket
            </button>
            </div>

          {/* Basket Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {/* Your New Basket */}
            <div className="bg-white rounded-lg p-2 pt-2 border-2 border-neutral-900 shadow-sm h-[140px] sm:h-[180px] flex flex-col relative col-span-1 opacity-0 animate-fade-in-1">
              <div className="absolute -top-2 -right-2">
                <div className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[8px] font-medium shadow-sm">
                  New
          </div>
              </div>

              {/* Basket Header */}
              <div className="flex justify-between items-start relative mb-0">
                <div className="flex items-start space-x-1.5">
                  <span className="text-base">🧺</span>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">
                      {basketName}
                    </h3>
                </div>
                </div>

                <div className="ml-auto">
                  <div className="bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full text-[8px]">
                    1 topic
                </div>
              </div>
            </div>

              {/* Topics Grid */}
              <div className="mt-2">
                <div className="inline-block bg-neutral-50 rounded-lg p-2">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 shadow-sm">
                      Trichomoniasis
          </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    {selectedConcept?.text}
                  </div>
                </div>
              </div>
            </div>

            {/* Terminal Ileum Basket */}
            <div 
              className="bg-white rounded-lg p-2 pt-2 border-2 border-neutral-200 shadow-sm h-[140px] sm:h-[180px] flex flex-col opacity-30"
            >
              <div className="flex justify-between items-start relative mb-0">
                <div className="flex items-start space-x-1.5">
                  <span className="text-base">🧺</span>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900">
                      Terminal Ileum
                    </h3>
                </div>
            </div>
                <div className="ml-auto">
                  <div className="bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full text-[8px]">
                    3 topics
                  </div>
                </div>
              </div>

              {/* Topics Grid */}
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                <div className="bg-neutral-50 rounded-lg p-1.5">
                  <div className="font-medium text-neutral-900 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 shadow-sm">
                    Vitamin B12
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    absorbed in terminal ileum
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-1.5">
                  <div className="font-medium text-neutral-900 text-xs bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 shadow-sm">
                    STEMI
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    12 hours fibrinolysis window
                  </div>
                </div>
              </div>
            </div>

            {/* Cut-off Baskets */}
            <div className="relative h-[70px] sm:h-[90px] overflow-hidden border-t-0">
              <div className="absolute inset-0 bg-white rounded-lg p-2 pt-2 border-2 border-neutral-200 shadow-sm opacity-30">
                <div className="flex justify-between items-start relative mb-0">
                  <div className="flex items-start space-x-1.5">
                    <span className="text-base">🧺</span>
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-900">
                        48 Hours
                      </h3>
                    </div>
                  </div>
                  <div className="ml-auto">
                    <div className="bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full text-[8px]">
                      2 topics
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-1.5 mt-2">
                  <div className="cursor-not-allowed transition-colors duration-200 
                              bg-neutral-100 hover:bg-neutral-200
                              rounded-lg border border-dashed border-neutral-300
                              flex items-center justify-center h-[40px] sm:h-[60px]">
                    <div className="text-center p-1.5">
                      <div className="text-neutral-600 font-medium text-xs mb-0.5">Topic 1</div>
                      <div className="text-neutral-400 text-[10px]">Click to reveal</div>
                    </div>
                  </div>
                  <div className="cursor-not-allowed transition-colors duration-200 
                              bg-neutral-100 hover:bg-neutral-200
                              rounded-lg border border-dashed border-neutral-300
                              flex items-center justify-center h-[40px] sm:h-[60px]">
                    <div className="text-center p-1.5">
                      <div className="text-neutral-600 font-medium text-xs mb-0.5">Topic 2</div>
                      <div className="text-neutral-400 text-[10px]">Click to reveal</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative h-[70px] sm:h-[90px] overflow-hidden border-t-0 hidden sm:block">
              <div className="absolute inset-0 bg-white rounded-lg p-2 pt-2 border-2 border-neutral-200 shadow-sm opacity-30">
                <div className="flex justify-between items-start relative mb-0">
                  <div className="flex items-start space-x-1.5">
                    <span className="text-base">🧺</span>
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-900">
                        Left
                      </h3>
                    </div>
                  </div>
                  <div className="ml-auto">
                    <div className="bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full text-[8px]">
                      2 topics
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-1.5 mt-2">
                  <div className="bg-neutral-50 rounded-lg p-1.5">
                    <div className="font-medium text-neutral-900 text-xs bg-rose-50 border border-rose-200 rounded-lg px-2 py-1 shadow-sm">
                      Carbon Monoxide
                    </div>
                    <div className="text-[10px] text-blue-600 mt-1">
                      causes left shift in...
                    </div>
                  </div>
                  <div className="cursor-not-allowed transition-colors duration-200 
                              bg-neutral-100 hover:bg-neutral-200
                              rounded-lg border border-dashed border-neutral-300
                              flex items-center justify-center h-[40px] sm:h-[60px]">
                    <div className="text-center p-1.5">
                      <div className="text-neutral-600 font-medium text-xs mb-0.5">Topic 2</div>
                      <div className="text-neutral-400 text-[10px]">Click to reveal</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Helper Text */}
        <p className="text-[10px] text-neutral-600 text-center mt-2">
          Your basket will always appear at the top when searching or browsing
        </p>
          </div>
        );
  };

  const renderBasketGrown = () => {
        return (
      <div className="space-y-3 max-w-lg mx-auto px-2">
        {/* Header */}
        <div className="bg-butter-50 rounded-lg p-2 border border-butter-200">
          <div className="flex items-start gap-1.5">
            <div className="text-base">🌱</div>
            <div className="space-y-1">
              <h3 className="font-semibold text-neutral-900 text-sm">See How Your Trigger Can Grow</h3>
              <p className="text-xs text-neutral-700">
                We've added a few more items from other subjects that connect to your basket/trigger and item.
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Basket Card */}
        <div className="bg-white rounded-lg p-2 pt-2 w-full sm:w-[420px] h-[280px] border-2 border-neutral-900 shadow-sm flex flex-col mx-auto">
          <div className="flex justify-between items-start relative mb-0">
            <div className="flex items-start space-x-1.5">
              <span className="text-base">🧺</span>
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  {basketName}
                </h3>
              </div>
            </div>
            <div className="ml-auto">
              <div className="bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded-full text-[8px]">
                3 items
              </div>
            </div>
          </div>

          {/* Topics Chain */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {/* Original Topic - No animation */}
            <div className="bg-neutral-50 rounded-lg p-2">
              <div className="inline-block">
                <div className="font-medium text-neutral-900 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 shadow-sm">
                  Trichomoniasis
                </div>
              </div>
              <div className="text-[10px] text-blue-600 mt-1">
                {selectedConcept?.text}
              </div>
            </div>

            {/* Connected Items - With animation */}
            {selectedConcept?.text.includes("protozoa") ? (
              <>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-1">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1 shadow-sm">
                      Metronidazole
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    no alcohol 48 hours after due to disulfiram-like reaction
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-2">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-purple-50 border border-purple-200 rounded-lg px-2 py-1 shadow-sm">
                      Disulfiram
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    contraindicated in ischemic heart disease and psychosis
                  </div>
                </div>
              </>
            ) : selectedConcept?.text.includes("Strawberry") ? (
              <>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-1">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-rose-50 border border-rose-200 rounded-lg px-2 py-1 shadow-sm">
                      Kawasaki Disease
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    Coronary aneurysm prevention: high-dose aspirin, IVIG
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-2">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-sky-50 border border-sky-200 rounded-lg px-2 py-1 shadow-sm">
                      Methotrexate
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    Caution: High-dose ASA (↓MTX excretion → toxicity)
                  </div>
                </div>
              </>
            ) : selectedConcept?.text.includes("discharge") ? (
              <>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-1">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-orange-50 border border-orange-200 rounded-lg px-2 py-1 shadow-sm">
                      Pseudomonas
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    produces Pyocyanin which gives its blue-green pigment
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-2">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-red-50 border border-red-200 rounded-lg px-2 py-1 shadow-sm">
                      Ceftazidime
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    a 3rd generation Cephalosporin used in Pseudomonas infections
                  </div>
                </div>
              </>
            ) : selectedConcept?.text.includes("metronidazole") ? (
              <>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-1">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-indigo-50 border border-indigo-200 rounded-lg px-2 py-1 shadow-sm">
                      CYP450 Inhibitors
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    - SICKFACES.COM; metronidazole
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-2">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-2 py-1 shadow-sm">
                      Halitosis
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    palliative management: metronidazole, antiseptic mouthwash
                  </div>
                </div>
              </>
            ) : selectedConcept?.text.includes("Sexually transmitted") ? (
              <>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-1">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-violet-50 border border-violet-200 rounded-lg px-2 py-1 shadow-sm">
                      Molluscum contagiosum
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    can be sexually transmitted
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-2 border border-dashed border-neutral-300 opacity-0 animate-fade-in-2">
                  <div className="inline-block">
                    <div className="font-medium text-neutral-900 text-xs bg-lime-50 border border-lime-200 rounded-lg px-2 py-1 shadow-sm">
                      Partner Notification Program
                    </div>
                  </div>
                  <div className="text-[10px] text-blue-600 mt-1">
                    notified if infected partner refuses/fails to inform susceptible partner within a specific time frame
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Memory Chain Visualization */}
        <div className="mt-1 overflow-x-auto -mx-2 px-2">
          <div className="bg-neutral-50 rounded-lg p-2 border border-neutral-200">
            <h4 className="text-[10px] font-medium text-neutral-900 mb-1">Memory Chain</h4>
            <div className="flex items-center space-x-1 pl-1 min-w-max">
              {/* Memory chain items */}
              <div className="px-2 py-1 bg-neutral-100 border border-neutral-900 rounded text-[10px] font-semibold text-neutral-900 relative mt-2">
                {basketName}
                <div className="absolute -top-2 -right-1">
                  <div className="bg-cranberry-600 text-white px-1.5 py-0.5 rounded text-[8px] font-medium shadow-sm">
                    Trigger
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowsUpDownIcon className="h-2 w-2 text-neutral-400 rotate-90" />
              </div>

              {/* Trichomoniasis */}
              <div className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] font-medium text-neutral-900">
                Trichomoniasis
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowsUpDownIcon className="h-2 w-2 text-neutral-400 rotate-90" />
              </div>

              {/* Third Item */}
              {selectedConcept?.text.includes("protozoa") ? (
                <div className="px-1.5 py-0.5 bg-yellow-50 border border-yellow-200 rounded text-[10px] font-medium text-neutral-900">
                  Metronidazole
                </div>
              ) : selectedConcept?.text.includes("Strawberry") ? (
                <div className="px-1.5 py-0.5 bg-rose-50 border border-rose-200 rounded text-[10px] font-medium text-neutral-900">
                  Kawasaki Disease
                </div>
              ) : selectedConcept?.text.includes("discharge") ? (
                <div className="px-1.5 py-0.5 bg-orange-50 border border-orange-200 rounded text-[10px] font-medium text-neutral-900">
                  Pseudomonas
                </div>
              ) : selectedConcept?.text.includes("metronidazole") ? (
                <>
                  <div className="px-1.5 py-0.5 bg-indigo-50 border border-indigo-200 rounded text-[10px] font-medium text-neutral-900">
                    CYP450 Inhibitors
                  </div>
                  <div className="flex items-center">
                    <ArrowsUpDownIcon className="h-2 w-2 text-neutral-400 rotate-90" />
                  </div>
                  <div className="px-1.5 py-0.5 bg-fuchsia-50 border border-fuchsia-200 rounded text-[10px] font-medium text-neutral-900">
                    Halitosis
                  </div>
                </>
              ) : selectedConcept?.text.includes("Sexually transmitted;") ? (
                <div className="px-1.5 py-0.5 bg-violet-50 border border-violet-200 rounded text-[10px] font-medium text-neutral-900">
                  Molluscum contagiosum
                </div>
              ) : null}

              {/* Arrow - Only show for non-metronidazole cases */}
              {!selectedConcept?.text.includes("metronidazole") && (
                <div className="flex items-center">
                  <ArrowsUpDownIcon className="h-2 w-2 text-neutral-400 rotate-90" />
                </div>
              )}

              {/* Fourth Item - Only show for non-metronidazole cases */}
              {!selectedConcept?.text.includes("metronidazole") && (
                <>
                  {selectedConcept?.text.includes("protozoa") ? (
                    <div className="px-1.5 py-0.5 bg-purple-50 border border-purple-200 rounded text-[10px] font-medium text-neutral-900">
                      Disulfiram
                    </div>
                  ) : selectedConcept?.text.includes("Strawberry") ? (
                    <div className="px-1.5 py-0.5 bg-sky-50 border border-sky-200 rounded text-[10px] font-medium text-neutral-900">
                      Methotrexate
                    </div>
                  ) : selectedConcept?.text.includes("discharge") ? (
                    <div className="px-1.5 py-0.5 bg-red-50 border border-red-200 rounded text-[10px] font-medium text-neutral-900">
                      Ceftazidime
                    </div>
                  ) : selectedConcept?.text.includes("Sexually transmitted;") ? (
                    <div className="px-1.5 py-0.5 bg-lime-50 border border-lime-200 rounded text-[10px] font-medium text-neutral-900">
                      Partner Notification Program
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Pro-tip */}
        <div className="mt-1">
          <div className="bg-butter-50 rounded-lg p-2 border border-butter-200">
            <div className="flex items-start gap-1.5">
              <span role="img" aria-label="tip" className="text-base">🤓</span>
              <div className="space-y-1">
                <p className="font-medium text-neutral-900 text-xs">Pro-Tip: Build Connections Strategically</p>
                <div className="space-y-0.5 text-[10px] text-neutral-700">
                  <p>Type keywords in search 🔎 for new items. Always target your hard concept 🎯 as the anchor point for placement.</p>
                  <p className="font-medium">Key rule: <span className="underline decoration-1">Link unfamiliar ideas TO familiar ones</span> (unfamiliar → familiar), never familiar TO unfamiliar (or worse, unfamiliar to unfamiliar)!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
        );
  };

  const handleAddToWorkspace = async () => {
    if (isAddingToWorkspace) return;
    setIsAddingToWorkspace(true);
    
    try {
      // Create workspace if it doesn't exist
      const { data: workspace, error: workspaceError } = await supabase
        .from('workspaces')
        .select('id')
        .eq('user_id', userId)
        .single();

      let workspaceId;
      if (workspaceError || !workspace) {
        const { data: newWorkspace, error: createError } = await supabase
          .from('workspaces')
          .insert([{ user_id: userId, name: 'my_workspace_name' }])
          .select('id')
          .single();

        if (createError) throw createError;
        workspaceId = newWorkspace.id;
      } else {
        workspaceId = workspace.id;
      }

      // Create both baskets
      const { data: baskets, error: basketError } = await supabase
        .from('baskets')
        .insert([
          {
            workspace_id: workspaceId,
            name: basketName,
            description: `topics linked to ${basketName}`,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            workspace_id: workspaceId,
            name: "terminal ileum",
            description: "topics linked to terminal ileum",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ])
        .select('id, name')
        .order('created_at');

      if (basketError) throw basketError;

      const userBasket = baskets.find(b => b.name === basketName);
      const terminalIleumBasket = baskets.find(b => b.name === "terminal ileum");

      // Get unique tags to create for both baskets
      const uniqueTags = [...new Set([
        // User's basket tags
        ...getItemContent(0).tag && [getItemContent(0).tag],
        ...getItemContent(1).tag && [getItemContent(1).tag],
        ...getItemContent(2).tag && [getItemContent(2).tag],
        // Terminal ileum basket tags
        'Gastroenterology',
        'Cardiology',
        'Genetics'
      ])].map(name => ({
        name,
        color: name === 'Microbiology' ? '#d7f6ec' :
               name === 'Pharmacology' ? '#fef3c7' :
               name === 'Ethics' ? '#e5e7eb' :
               name === 'Palliative Care' ? '#ddd6fe' :
               name === 'Paediatrics' ? '#fce7f3' :
               name === 'Gastroenterology' ? '#dbeafe' :
               name === 'Cardiology' ? '#fee2e2' :
               name === 'Genetics' ? '#f3e8ff' :
               '#fee2e2', // default color
        workspace_id: workspaceId
      }));

      // Create tags
      const { data: tags, error: tagsError } = await supabase
        .from('tags')
        .insert(uniqueTags)
        .select('id, name');

      if (tagsError) throw tagsError;

      // Create topics for user's basket
      const userBasketTopics = [
        {
          name: getItemContent(0).topic,
          relation_description: getItemContent(0).relation_description,
          basket_id: userBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 0,
          tag_id: tags.find(t => t.name === getItemContent(0).tag)?.id
        },
        {
          name: getItemContent(1).topic,
          relation_description: getItemContent(1).relation_description,
          basket_id: userBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 1,
          tag_id: tags.find(t => t.name === getItemContent(1).tag)?.id
        },
        {
          name: getItemContent(2).topic,
          relation_description: getItemContent(2).relation_description,
          basket_id: userBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 2,
          tag_id: tags.find(t => t.name === getItemContent(2).tag)?.id
        }
      ];

      // Create topics for terminal ileum basket
      const terminalIleumTopics = [
        {
          name: "Vitamin B12",
          relation_description: "linked to basket name",
          basket_id: terminalIleumBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 0,
          tag_id: tags.find(t => t.name === 'Gastroenterology')?.id
        },
        {
          name: "STEMI",
          relation_description: "12 hrs like 12 in Vitamin B12",
          basket_id: terminalIleumBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 1,
          tag_id: tags.find(t => t.name === 'Cardiology')?.id
        },
        {
          name: "Marfan syndrome",
          relation_description: "upward like ST segment in STEMI",
          basket_id: terminalIleumBasket.id,
          user_id: userId,
          workspace_id: workspaceId,
          order_index: 2,
          tag_id: tags.find(t => t.name === 'Genetics')?.id
        }
      ];

      // Create all topics
      const { data: topics, error: topicsError } = await supabase
        .from('topics')
        .insert([...userBasketTopics, ...terminalIleumTopics])
        .select('id, basket_id')
        .order('basket_id, order_index');

      if (topicsError) throw topicsError;

      // Group topics by basket
      const userBasketTopicIds = topics
        .filter(t => t.basket_id === userBasket.id)
        .map(t => t.id);
      const terminalIleumTopicIds = topics
        .filter(t => t.basket_id === terminalIleumBasket.id)
        .map(t => t.id);

      // Create concepts for user's basket
      const userBasketConcepts = userBasketTopicIds.map((topicId, index) => ({
        text: getItemContent(index).concept,
        is_highlighted: true,
        topic_id: topicId
      }));

      // Create concepts for terminal ileum basket
      const terminalIleumConcepts = [
        {
          text: "absorbed in the terminal ileum",
          is_highlighted: true,
          topic_id: terminalIleumTopicIds[0]
        },
        {
          text: "Fibrinolysis within 12 hours",
          is_highlighted: true,
          topic_id: terminalIleumTopicIds[1]
        },
        {
          text: "Upward lens dislocation",
          is_highlighted: true,
          topic_id: terminalIleumTopicIds[2]
        }
      ];

      // Create all concepts
      const { error: conceptsError } = await supabase
        .from('concepts')
        .insert([...userBasketConcepts, ...terminalIleumConcepts]);

      if (conceptsError) throw conceptsError;

      // Success - set the flag and move to next step
      setBasketAddedToWorkspace(true);
      handleNext();
    } catch (error) {
      console.error('Error adding to workspace:', error);
      // Show error toast to user
      toast.error(error.message || 'Failed to add baskets to workspace. Please try again.');
    } finally {
      setIsAddingToWorkspace(false);
    }
  };

  // Modified close handler
  const handleClose = () => {
    // If we're in the finish step, handle it like clicking Done
    if (currentStep === tourSteps.length - 1) {
      handleComplete();
      return;
    }
    
    // Only show confirmation if basket hasn't been added to workspace
    if (!basketAddedToWorkspace) {
      setShowExitModal(true);
    } else {
      onClose();
    }
  };

  // Handle confirmed exit
  const handleConfirmedExit = async () => {
    try {
      // Delete the incomplete tour
      const { error } = await supabase
        .from('onboarding_tours')
        .delete()
        .eq('id', tourId);

      if (error) throw error;
      
      setShowExitModal(false);
      onClose();
    } catch (error) {
      console.error('Error exiting tour:', error);
      toast.error('Failed to exit tutorial. Please try again.');
    }
  };

  if (!isOpen) return null;

  const step = tourSteps[currentStep];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 z-50">
        <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg w-full max-w-xl">
        {/* Header */}
          <div className="px-3 py-2 border-b-2 border-neutral-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-neutral-900">
            {step.title}
          </h3>
          <button
              onClick={handleClose}
              className="p-1 hover:bg-neutral-100 rounded-lg transition-colors"
          >
              <XMarkIcon className="h-4 w-4 text-neutral-600" />
          </button>
        </div>

        {/* Content */}
          <div className="px-3 py-3">
            <div className="flex flex-col items-center space-y-2">
            {renderStepContent()}

            {/* Progress Dots */}
              <div className="flex space-x-1">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                    className={`w-1.5 h-1.5 rounded-full ${
                    index === currentStep
                      ? 'bg-cranberry-600'
                      : 'bg-neutral-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
          <div className="px-3 py-2 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200">
            <div className="flex items-center">
              <div className="flex space-x-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                    className="flex items-center px-3 py-1.5 text-sm text-neutral-900 hover:text-cranberry-700 transition-colors"
                >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
              )}
              </div>
              <div className="flex-1 flex items-center justify-end gap-2">
              {currentStep < tourSteps.length - 1 ? (
                  <>
                <button
                      onClick={step.type === 'works' ? handleAddToWorkspace : handleNext}
                      disabled={
                        (step.type === 'topic' && !concepts.some(c => c.is_highlighted)) ||
                        (step.type === 'basket-name' && !basketName.trim()) ||
                        (step.type === 'study-mode' && !isRevealed) ||
                        (step.type === 'works' && !revealedItems.every(item => item)) ||
                        isAddingToWorkspace
                      }
                      className={`
                        flex items-center px-3 py-1.5 text-sm text-white rounded-xl border-2 font-medium transition-colors relative group
                        ${step.type === 'topic' && !concepts.some(c => c.is_highlighted)
                          ? 'bg-cranberry-700/30 border-cranberry-700/30 cursor-not-allowed'
                          : step.type === 'basket-name' && !basketName.trim()
                          ? 'bg-cranberry-700/50 border-neutral-400 cursor-not-allowed'
                          : step.type === 'study-mode' && !isRevealed
                          ? 'bg-cranberry-700/50 border-neutral-400 cursor-not-allowed'
                          : step.type === 'works' && !revealedItems.every(item => item)
                          ? 'bg-cranberry-700/50 border-neutral-400 cursor-not-allowed'
                          : isAddingToWorkspace
                          ? 'bg-cranberry-700/50 border-neutral-400 cursor-not-allowed'
                          : 'bg-cranberry-700 border-neutral-900 hover:bg-cranberry-600'
                        }
                      `}
                    >
                      {step.type === 'topic' ? "Add to Basket or Memory Trigger" : 
                       step.type === 'basket-name' ? "View your first basket" :
                       step.type === 'basket-preview' ? "Let's test the trigger" :
                       step.type === 'study-mode' ? (isRevealed ? "Grow This Basket" : "Next") :
                       step.type === 'basket-grown' ? "See Why This Works" :
                       step.type === 'works' ? (isAddingToWorkspace ? "Adding to workspace..." : "Got it") :
                       (step.buttonText || "Next")}
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
                  </>
              ) : (
                  <div className="flex gap-2">
                <button
                  onClick={handleComplete}
                      className="flex items-center px-3 py-1.5 text-sm text-white rounded-xl border-2 border-neutral-900 font-medium transition-colors bg-cranberry-700 hover:bg-cranberry-600"
                >
                      Done
                </button>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg w-full max-w-md p-4">
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-neutral-900">
                Exit Tutorial?
              </h3>
              <p className="text-sm text-neutral-600 text-left">
                Your progress will be reset if you exit now. Are you sure you want to leave?
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowExitModal(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
              >
                Continue Tutorial
              </button>
              <button
                onClick={handleConfirmedExit}
                className="px-4 py-2 text-sm font-medium text-white bg-cranberry-600 hover:bg-cranberry-700 rounded-lg transition-colors"
              >
                Exit Tutorial
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingTour; 